import { StickyNote, Pencil, Trash2 } from 'lucide-react'
import { FormProvider } from 'react-hook-form'
import { useOrderNotesTable } from '@/hooks/features/order-note/useOrderNotesTable'
import { useDataTable } from '@/hooks/ui'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { ColumnVisibilityDropdown, DataTable, NoData } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination-controls'
import { Switch } from '@/components/ui/switch'
import { ActionDropdown, ConfirmDialog, PageHeader, StatsCards, StatusBadge } from '@/components/atoms'
import { OrderNoteModel } from '@/types'
import { OrderNoteFilters } from './OrderNoteFilters'
import { OrderNoteDialog } from './OrderNoteDialog'

type TableColumns = 'content' | 'status' | 'orders_count' | 'actions'

const DEFAULT_VISIBLE_COLUMNS: TableColumns[] = [
    'content',
    'status',
    'orders_count',
    'actions',
]

export const OrderNotesTable = () => {
    const {
        loading,
        data,
        pagination,
        formMethods,
        stats,
        mode,
        orderNoteDialogOpen,
        editOrderNote,
        deleteDialogOpen,
        orderNoteToDelete,
        createLoading,
        updateLoading,
        deleteLoading,
        setPage,
        setPageSize,
        openCreateDialog,
        openEditDialog,
        closeDialog,
        handleDeleteClick,
        handleDeleteConfirm,
        setDeleteDialogOpen,
        handleCreateOrderNote,
        handleUpdateOrderNote,
        toggleStatus,
    } = useOrderNotesTable()

    const { columns, changeColumnVisibility, disabledColumns } = useDataTable<
        OrderNoteModel.OrderNote,
        TableColumns
    >({
        columns: [
            {
                accessorKey: 'content',
                columnLabel: () => 'Note',
                cell: (data) => <span className="block max-w-md truncate font-medium text-ink">{data?.content}</span>,
                hasSorting: true,
            },
            {
                accessorKey: 'status',
                columnLabel: () => 'Status',
                cell: (data) => (
                    <div className="flex items-center gap-2">
                        <StatusBadge status={data?.status === 'active' ? 'active' : 'inactive'} />
                        <Switch
                            checked={data?.status === 'active'}
                            onCheckedChange={() => toggleStatus(data)}
                        />
                    </div>
                ),
            },
            {
                accessorKey: 'orders_count',
                columnLabel: () => 'Orders',
                cell: (data) => <span className="tabular-nums text-ink">{data?.orders_count || 0}</span>,
            },
            {
                accessorKey: 'actions',
                columnLabel: () => 'Actions',
                cell: (data) => (
                    <div className="flex justify-end">
                        <ActionDropdown
                            items={[
                                {
                                    label: 'Edit',
                                    icon: Pencil,
                                    onClick: () => openEditDialog(data),
                                },
                                {
                                    label: 'Delete',
                                    icon: Trash2,
                                    variant: 'destructive',
                                    onClick: () => handleDeleteClick(data),
                                },
                            ]}
                        />
                    </div>
                ),
                classes: () => ({
                    column: 'text-right',
                    cell: 'text-right',
                }),
                isAction: true,
            },
        ],
        visibleColumns: DEFAULT_VISIBLE_COLUMNS,
        disabledColumns: ['actions'],
    })

    return (
        <div className="space-y-4">
            <PageHeader
                title="Order notes"
                description="Preset notes you can attach to orders."
                onAddClick={openCreateDialog}
                addButtonText="Add note"
            />

            <StatsCards
                stats={[
                    {
                        title: 'Total notes',
                        value: stats.totalOrderNotes,
                        icon: StickyNote,
                    },
                    {
                        title: 'Active notes',
                        value: stats.activeOrderNotes,
                    },
                    {
                        title: 'Inactive notes',
                        value: stats.inactiveOrderNotes,
                    },
                ]}
            />

            <Card>
                <CardHeader>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                            <Pagination.PageSizeDropdown
                                pageSize={pagination.pageSize}
                                onPageSizeChange={setPageSize}
                            />
                            <FormProvider {...formMethods}>
                                <OrderNoteFilters />
                            </FormProvider>
                        </div>
                        <ColumnVisibilityDropdown
                            columns={columns}
                            onColumnVisibilityChange={changeColumnVisibility}
                            disabledColumns={disabledColumns}
                        />
                    </div>
                </CardHeader>
                <CardContent>
                    {!loading && data.length === 0 ? (
                        <NoData
                            title="No order notes yet"
                            btnText="Add note"
                            openCreateDialog={openCreateDialog}
                        />
                    ) : (
                        <>
                            <DataTable columns={columns} data={data} loading={loading} />
                            <Pagination.Buttons
                                currentPage={pagination.currentPage}
                                totalItems={pagination.totalCount}
                                pageSize={pagination.pageSize}
                                onPageChange={setPage}
                            />
                        </>
                    )}
                </CardContent>
            </Card>

            <OrderNoteDialog
                open={orderNoteDialogOpen}
                mode={mode}
                editOrderNote={editOrderNote}
                closeDialog={closeDialog}
                onCreate={handleCreateOrderNote}
                onUpdate={handleUpdateOrderNote}
                loading={createLoading || updateLoading}
            />

            <ConfirmDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                title="Delete note?"
                description={`"${orderNoteToDelete?.content.slice(0, 30)}…" will be deleted. This can't be undone.`}
                confirmText={deleteLoading ? 'Deleting…' : 'Delete note'}
                onConfirm={handleDeleteConfirm}
                loading={deleteLoading}
                variant="destructive"
            />
        </div>
    )
}
